import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

import { Spinner } from '@/components/ui/spinner';

const processedNaverCodes = new Set<string>();

export const NaverCallback: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasHandledCallbackRef = useRef(false);

  const redirectToLoginWithError = useCallback((message: string, delayMs: number = 800) => {
    sessionStorage.setItem('socialLoginError', message);

    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = setTimeout(() => {
      navigate(`/login?error=${encodeURIComponent(message)}`, { replace: true });
    }, delayMs);
  }, [navigate]);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code) {
      const errorMsg = '인증 코드가 없습니다.';
      setError(errorMsg);
      redirectToLoginWithError(errorMsg);
      return;
    }

    if (!state) {
      const errorMsg = 'state 값이 없습니다.';
      setError(errorMsg);
      redirectToLoginWithError(errorMsg);
      return;
    }

    if (hasHandledCallbackRef.current) {
      return;
    }
    hasHandledCallbackRef.current = true;

    const processedKey = `oauth:naver:processed:${code}`;
    const alreadyProcessed = processedNaverCodes.has(code) || sessionStorage.getItem(processedKey) === '1';

    if (alreadyProcessed) {
      const hasAuthToken = !!(localStorage.getItem('authToken') || sessionStorage.getItem('authToken'));
      if (hasAuthToken) {
        const redirectUrl = sessionStorage.getItem('naverLoginRedirect') || '/dashboard';
        sessionStorage.removeItem('naverLoginRedirect');
        navigate(redirectUrl, { replace: true });
        return;
      }

      const savedError = sessionStorage.getItem('socialLoginError');
      if (savedError) {
        redirectToLoginWithError(savedError, 0);
      }
      return;
    }

    processedNaverCodes.add(code);
    sessionStorage.setItem(processedKey, '1');

    const handleNaverCallback = async () => {
      try {
        // 세션 스토리지에서 저장된 state 확인 (CSRF 방지)
        const savedState = sessionStorage.getItem('naverLoginState');
        if (state !== savedState) {
          const errorMsg = '보안 검증 실패: state 값이 일치하지 않습니다.';
          setError(errorMsg);
          sessionStorage.removeItem('naverLoginState');
          redirectToLoginWithError(errorMsg);
          return;
        }
        sessionStorage.removeItem('naverLoginState');

        // 회원가입 컨텍스트에서 저장된 동의 정보 읽기
        const isSignupContext = sessionStorage.getItem('naverSignupContext') === 'true';
        sessionStorage.removeItem('naverSignupContext');
        let consentData: { agreeTerms?: boolean; agreePrivacy?: boolean; agreeMarketing?: boolean } | undefined;
        if (isSignupContext) {
          const raw = sessionStorage.getItem('socialPreOAuthConsent');
          sessionStorage.removeItem('socialPreOAuthConsent');
          if (raw) {
            try { consentData = JSON.parse(raw); } catch { /* ignore */ }
          }
        }

        // StrictMode의 useEffect 이중 실행으로 인한 중복 callback 호출을 방지한다.
        await login(code, state, false, 'naver', consentData);

        // 리다이렉트 URL 확인
        const redirectUrl = sessionStorage.getItem('naverLoginRedirect') || '/dashboard';
        navigate(redirectUrl, { replace: true });
        // navigate 후 sessionStorage 제거
        if (redirectUrl !== '/dashboard') {
          sessionStorage.removeItem('naverLoginRedirect');
        }
      } catch (err: any) {
        // 신규 유저: 회원가입 페이지에서 동의 후 네이버 소셜 가입을 진행하도록 안내
        if (err.code === 'SOCIAL_NEW_USER' || err.data?.errorType === 'SOCIAL_NEW_USER') {
          navigate('/signup', { state: { autoOpenConsent: 'naver' }, replace: true });
          return;
        }
        // 승인 대기 신청 완료 (이미 가입된 유저)
        if (
          err.code === 'SOCIAL_SIGNUP_PENDING' ||
          err.message?.startsWith('SOCIAL_SIGNUP_PENDING|') ||
          err.data?.errorType === 'SOCIAL_SIGNUP_PENDING'
        ) {
          const rawMsg = err.message || '';
          const userMsg = rawMsg.startsWith('SOCIAL_SIGNUP_PENDING|')
            ? rawMsg.substring('SOCIAL_SIGNUP_PENDING|'.length)
            : '회원가입 신청이 완료되었습니다. 관리자 승인 후 이용 가능합니다.';
          const pendingEmail = err.data?.email || err.data?.data?.email;
          navigate('/pending-approval', { state: { message: userMsg, email: pendingEmail }, replace: true });
          return;
        }
        // ✅ 이슈 #4: OAuth code 재사용 에러 처리 (B/E OAUTH_CODE_ALREADY_USED 반환 시)
        if (err.code === 'OAUTH_CODE_ALREADY_USED' || err.data?.errorType === 'OAUTH_CODE_ALREADY_USED') {
          redirectToLoginWithError('이미 처리된 로그인 요청입니다. 다시 시도해주세요.', 0);
          return;
        }
        const errorMsg = err.message || '네이버 로그인 처리 중 오류가 발생했습니다.';
        setError(errorMsg);
        redirectToLoginWithError(errorMsg);
      }
    };

    handleNaverCallback();

    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, [searchParams, login, navigate, redirectToLoginWithError]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        {error ? (
          <div className="text-red-600">
            <p className="mb-4">{error}</p>
            <p className="text-sm text-gray-600">잠시 후 로그인 페이지로 이동합니다...</p>
          </div>
        ) : (
          <div>
            <Spinner size="2xl" className="mx-auto mb-4 text-primary" />
            <p className="text-gray-600">네이버 로그인 처리 중...</p>
          </div>
        )}
      </div>
    </div>
  );
};
